import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  Timestamp,
  orderBy,
} from 'firebase/firestore';
import { db, getDoc } from '@/lib/firebase';
import { Follow, FollowCounts, User } from '@/types';
import { getUser } from './user.service';
import { logger } from '@/lib/logger';

/**
 * Follow a user
 */
export const followUser = async (
  followerId: string,
  followedId: string
): Promise<void> => {
  try {
    // Prevent self-follow
    if (followerId === followedId) {
      throw new Error('Cannot follow yourself');
    }

    // Check if already following
    const isAlreadyFollowing = await isFollowing(followerId, followedId);
    if (isAlreadyFollowing) {
      return; // Already following, no-op
    }

    // Create follow document with composite ID
    const followId = `${followerId}_${followedId}`;
    const followRef = doc(db, 'follows', followId);

    const followData: Follow = {
      id: followId,
      followerId,
      followedId,
      createdAt: Timestamp.now(),
    };

    await setDoc(followRef, followData);
    logger('follow').log(`User ${followerId} followed ${followedId}`);
  } catch (error) {
    logger('follow').error('Error following user:', error);
    throw error;
  }
};

/**
 * Unfollow a user
 */
export const unfollowUser = async (
  followerId: string,
  followedId: string
): Promise<void> => {
  try {
    const followId = `${followerId}_${followedId}`;
    const followRef = doc(db, 'follows', followId);

    // Check if follow exists
    const followDoc = await getDoc(followRef);
    if (!followDoc.exists()) {
      return; // Not following, no-op
    }

    await deleteDoc(followRef);
    logger('follow').log(`User ${followerId} unfollowed ${followedId}`);
  } catch (error) {
    logger('follow').error('Error unfollowing user:', error);
    throw error;
  }
};

/**
 * Check if a user is following another user
 */
export const isFollowing = async (
  followerId: string,
  followedId: string
): Promise<boolean> => {
  try {
    const followId = `${followerId}_${followedId}`;
    const followRef = doc(db, 'follows', followId);
    const followDoc = await getDoc(followRef);
    return followDoc.exists();
  } catch (error) {
    logger('follow').error('Error checking follow status:', error);
    return false;
  }
};

/**
 * Get list of users following a specific user (followers)
 */
export const getFollowers = async (userId: string): Promise<User[]> => {
  try {
    const followsRef = collection(db, 'follows');
    const q = query(
      followsRef,
      where('followedId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const followerIds = querySnapshot.docs.map(
      (doc) => doc.data().followerId as string
    );

    // Fetch user data for each follower
    const followers = await Promise.all(
      followerIds.map((uid) => getUser(uid))
    );

    // Filter out null values (users that might have been deleted)
    return followers.filter((user): user is User => user !== null);
  } catch (error) {
    logger('follow').error('Error getting followers:', error);
    throw error;
  }
};

/**
 * Get list of users a specific user is following (following)
 */
export const getFollowing = async (userId: string): Promise<User[]> => {
  try {
    const followsRef = collection(db, 'follows');
    const q = query(
      followsRef,
      where('followerId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const followedIds = querySnapshot.docs.map(
      (doc) => doc.data().followedId as string
    );

    // Fetch user data for each followed user
    const following = await Promise.all(
      followedIds.map((uid) => getUser(uid))
    );

    // Filter out null values (users that might have been deleted)
    return following.filter((user): user is User => user !== null);
  } catch (error) {
    logger('follow').error('Error getting following:', error);
    throw error;
  }
};

/**
 * Get follower and following counts for a user
 */
export const getFollowCounts = async (
  userId: string
): Promise<FollowCounts> => {
  try {
    const followsRef = collection(db, 'follows');

    // Get followers count
    const followersQuery = query(
      followsRef,
      where('followedId', '==', userId)
    );
    const followersSnapshot = await getDocs(followersQuery);
    const followersCount = followersSnapshot.size;

    // Get following count
    const followingQuery = query(
      followsRef,
      where('followerId', '==', userId)
    );
    const followingSnapshot = await getDocs(followingQuery);
    const followingCount = followingSnapshot.size;

    return {
      followersCount,
      followingCount,
    };
  } catch (error) {
    logger('follow').error('Error getting follow counts:', error);
    throw error;
  }
};

